import { setup, assign } from 'xstate';

export type RegistrationRole = 'candidate' | 'employer' | 'both';
export type ProfileType = 'Individual' | 'Organisation';

export interface AccountData {
  email: string;
  password: string;
  firstName: string;
  lastName: string;
}

export interface RegistrationContext {
  role: RegistrationRole | null;
  account: AccountData | null;
  profileType: ProfileType | null;
  nationalId: { type: string; number: string; country: string } | null;
  headline: string;
  availability: string;
  workType: string;
  province: string;
  city: string;
  skills: string[];
  submissionError: string | null;
}

export const emptyRegistrationContext: RegistrationContext = {
  role: null,
  account: null,
  profileType: null,
  nationalId: null,
  headline: '',
  availability: '',
  workType: 'Contract',
  province: '',
  city: '',
  skills: [],
  submissionError: null,
};

export type RegistrationEvent =
  | { type: 'START' }
  | { type: 'SELECT_ROLE'; role: RegistrationRole }
  | { type: 'BACK' }
  | { type: 'SUBMIT_ACCOUNT'; account: AccountData }
  | { type: 'SELECT_PROFILE_TYPE'; profileType: ProfileType }
  | {
      type: 'SUBMIT_PROFILE';
      headline: string;
      availability: string;
      workType: string;
      province: string;
      city: string;
    }
  | { type: 'SUBMIT_NATIONAL_ID'; nationalId: { type: string; number: string; country: string } | null }
  | { type: 'ADD_SKILLS'; skills: string[] }
  | { type: 'SKIP_SKILLS' }
  | { type: 'EDIT_ROLE' }
  | { type: 'EDIT_ACCOUNT' }
  | { type: 'EDIT_PROFILE' }
  | { type: 'EDIT_NATIONAL_ID' }
  | { type: 'EDIT_SKILLS' }
  | { type: 'SUBMIT' }
  | { type: 'SUBMITTED' }
  | { type: 'ERROR'; message?: string };

export const registrationMachine = setup({
  types: {
    context: {} as RegistrationContext,
    events: {} as RegistrationEvent,
  },
  guards: {
    needsCandidateSkills: ({ context }) => context.role !== 'employer',
  },
}).createMachine({
  id: 'registration',
  context: emptyRegistrationContext,
  initial: 'idle',
  states: {
    idle: {
      on: { START: 'roleSelection' },
    },
    roleSelection: {
      on: {
        SELECT_ROLE: {
          target: 'accountDetails',
          actions: assign({
            role: ({ event }) => event.role,
          }),
        },
        BACK: 'idle',
      },
    },
    accountDetails: {
      on: {
        SUBMIT_ACCOUNT: {
          target: 'profileType',
          actions: assign({
            account: ({ event }) => event.account,
          }),
        },
        BACK: 'roleSelection',
      },
    },
    profileType: {
      on: {
        SELECT_PROFILE_TYPE: {
          target: 'basicProfile',
          actions: assign({
            profileType: ({ event }) => event.profileType,
          }),
        },
        BACK: 'accountDetails',
      },
    },
    basicProfile: {
      on: {
        SUBMIT_PROFILE: [
          {
            target: 'nationalId',
            guard: 'needsCandidateSkills',
            actions: assign({
              headline: ({ event }) => event.headline,
              availability: ({ event }) => event.availability,
              workType: ({ event }) => event.workType,
              province: ({ event }) => event.province,
              city: ({ event }) => event.city,
            }),
          },
          {
            target: 'nationalId',
            actions: assign({
              headline: ({ event }) => event.headline,
              availability: ({ event }) => event.availability,
              workType: ({ event }) => event.workType,
              province: ({ event }) => event.province,
              city: ({ event }) => event.city,
            }),
          },
        ],
        BACK: 'profileType',
      },
    },
    nationalId: {
      on: {
        SUBMIT_NATIONAL_ID: [
          {
            target: 'skillSelection',
            guard: 'needsCandidateSkills',
            actions: assign({
              nationalId: ({ event }) => event.nationalId,
            }),
          },
          {
            target: 'review',
            actions: assign({
              nationalId: ({ event }) => event.nationalId,
            }),
          },
        ],
        BACK: 'basicProfile',
      },
    },
    skillSelection: {
      on: {
        ADD_SKILLS: {
          target: 'review',
          actions: assign({ skills: ({ event }) => event.skills }),
        },
        SKIP_SKILLS: 'review',
        BACK: 'nationalId',
      },
    },
    review: {
      on: {
        EDIT_ROLE: 'roleSelection',
        EDIT_ACCOUNT: 'accountDetails',
        EDIT_PROFILE: 'basicProfile',
        EDIT_NATIONAL_ID: 'nationalId',
        EDIT_SKILLS: 'skillSelection',
        SUBMIT: 'submitting',
        BACK: 'skillSelection',
      },
    },
    submitting: {
      on: {
        SUBMITTED: 'complete',
        ERROR: {
          target: 'review',
          actions: assign({
            submissionError: ({ event }) => event.message ?? 'Registration failed',
          }),
        },
      },
    },
    complete: {
      type: 'final',
    },
  },
});

export const registrationSteps = [
  'roleSelection',
  'accountDetails',
  'profileType',
  'basicProfile',
  'nationalId',
  'skillSelection',
  'review',
] as const;
